import { useEffect } from "react";
import { Navigate, useLocation } from "react-router";
import { Button } from "@/components/ui/button";
import { Panel } from "@/components/ui/Panel";
import { useAuth } from "@/contexts/AuthContext";
// The wordmark is outlined paths, so the <img> needs no webfont.
import patentStudioLogo from "@/assets/logo-patent-studio-for-light.svg";

/**
 * The sign-in page.
 *
 * Credentials are never handled here: the button hands the browser to the
 * identity provider, which owns the password, the multi-factor challenge and
 * the account recovery path. What is left is somewhere to land after signing
 * out, and something to click when the automatic hand-off does not stick.
 */

/**
 * Marks that this page has already sent the browser to the provider once.
 *
 * Arriving here unauthenticated normally means a guard turned someone away, so
 * going straight to the provider saves a pointless click. That convenience is
 * also a loop waiting to happen: any failure that returns the browser here
 * still unauthenticated would send it straight back out again, forever, with
 * no way to read whatever went wrong. Redirecting at most once per browser
 * session keeps the convenience and bounds the failure to a single round trip,
 * after which the button is the only way onward.
 *
 * Session-scoped rather than in-memory because the redirect is a full page
 * load, which discards anything held in memory — that is exactly why the naive
 * version loops.
 */
const HANDOFF_ATTEMPTED = "auth:handoff-attempted";

function readAttempted(): boolean {
  try {
    return sessionStorage.getItem(HANDOFF_ATTEMPTED) !== null;
  } catch {
    // Storage can be unavailable (private mode, blocked cookies). Treating
    // that as "already attempted" costs one click and cannot loop, which is
    // the right way round to fail.
    return true;
  }
}

function markAttempted(): void {
  try {
    sessionStorage.setItem(HANDOFF_ATTEMPTED, "1");
  } catch {
    /* see readAttempted */
  }
}

function clearAttempted(): void {
  try {
    sessionStorage.removeItem(HANDOFF_ATTEMPTED);
  } catch {
    /* see readAttempted */
  }
}

export function LoginPage() {
  const { isAuthenticated, isLoading, login } = useAuth();
  const location = useLocation();
  // Why the last attempt did not finish, if the API sent them back here to be
  // told. The API filters this to plain text before it reaches the URL.
  const refusal = new URLSearchParams(location.search).get("error_description");
  // Where the guard turned this person away from, so they land back on the
  // page they actually asked for rather than the dashboard.
  const from = (location.state as { from?: string } | null)?.from ?? "/";

  useEffect(() => {
    if (isLoading) return;
    if (isAuthenticated) {
      clearAttempted();
      return;
    }
    // Never hand straight back to the provider when the last attempt has just
    // come back refused: that is the loop, and the reason would flash past
    // unread. The attempt marker bounds it anyway; this makes it deliberate.
    if (refusal) return;
    if (readAttempted()) return;
    markAttempted();
    login(from);
  }, [isLoading, isAuthenticated, login, from, refusal]);

  if (isAuthenticated) {
    return <Navigate to={from} replace />;
  }

  return (
    <div className="flex min-h-screen items-center justify-center bg-muted/30 p-4">
      <Panel pad="none" className="w-full max-w-sm">
        <div className="flex flex-col items-center space-y-1.5 p-4 pb-0 text-center">
          <img
            src={patentStudioLogo}
            alt="Patent Studio Pilot"
            className="h-9 max-w-fit"
          />
          <p className="text-sm text-muted-foreground">
            {isLoading ? "Checking your session..." : "Sign in to continue"}
          </p>
          {refusal && (
            <p role="alert" className="pt-2 text-sm text-destructive">
              {refusal}
            </p>
          )}
        </div>
        <div className="p-4">
          <Button
            className="w-full"
            onClick={() => {
              markAttempted();
              login(from);
            }}
            disabled={isLoading}
          >
            Sign in
          </Button>
        </div>
      </Panel>
    </div>
  );
}
